
import OverlayPanel from "primevue/overlaypanel";
import Icons from "@/app/ui/components/icons/index.vue";
import { Options, prop, Vue } from "vue-class-component";

class Props {
  item = prop<any>({ required: true });
  columnName = prop<string>({ required: true });
  dexReasons = prop<any[]>({ required: false });
  disputeReasons = prop<any[]>({ required: false });
  threeLcCodesOptions = prop<any[]>({ required: false });
  getDexReasonCodes = prop<Function>({ required: false });
  getDisputeReasonCodes = prop<Function>({ required: false });
  getAllReasonNames = prop<Function>({ required: true });
}

@Options({
  name: "ReasonPopover",
  components: { OverlayPanel, Icons }
})
export default class ReasonPopover extends Vue.with(Props) {
  currentPage = 1;
  itemsPerPage = 5;

  // Helper to get reason data (reusable for DEX and dispute reasons)
  private getReasonData(params: {
    getReasonCodes: Function | undefined;
    reasons: any[] | undefined;
    title: string;
  }): {
    reasonCodes: string[];
    options: any[];
    title: string;
  } | null {
    if (!params.getReasonCodes || !params.reasons) {
      return null;
    }
    return {
      reasonCodes: params.getReasonCodes(this.item),
      options: params.reasons || [],
      title: params.title
    };
  }

  // Helper to get 3LC data
  private getThreeLcData(): {
    reasonCodes: string[];
    options: any[];
    title: string;
  } | null {
    if (!this.threeLcCodesOptions) {
      return null;
    }
    return {
      reasonCodes:
        this.item.dexBucketTicketAccount?.dbtaThreeLc ||
        this.item.dbtaThreeLc ||
        [],
      options: this.threeLcCodesOptions || [],
      title: "3LC Ditugaskan"
    };
  }

  // Helper to get column data based on column name
  private getColumnData(): {
    reasonCodes: string[];
    options: any[];
    title: string;
  } | null {
    if (this.columnName === "Alasan Tiket DEX") {
      return this.getReasonData({
        getReasonCodes: this.getDexReasonCodes,
        reasons: this.dexReasons,
        title: "Alasan tiket DEX"
      });
    } else if (this.columnName === "Alasan Tiket Sanggah") {
      return this.getReasonData({
        getReasonCodes: this.getDisputeReasonCodes,
        reasons: this.disputeReasons,
        title: "Alasan tiket sanggah"
      });
    } else if (this.columnName === "3LC Ditugaskan") {
      return this.getThreeLcData();
    }
    return null;
  }

  get popoverData() {
    const columnData = this.getColumnData();
    if (!columnData) {
      return null;
    }

    const allReasonNames = this.getAllReasonNames(
      columnData.reasonCodes,
      columnData.options
    );

    return {
      key: `${this.columnName}-${this.item.account_id}`,
      list: allReasonNames,
      maxDisplay: 1,
      title: columnData.title
    };
  }

  get displayList() {
    return this.popoverData?.list?.slice(0, this.popoverData.maxDisplay) || [];
  }

  get remainingCount() {
    if (!this.popoverData) return 0;
    return this.popoverData.list.length - this.popoverData.maxDisplay;
  }

  get totalPages() {
    if (!this.popoverData) return 0;
    return Math.ceil(this.popoverData.list.length / this.itemsPerPage);
  }

  get paginatedList() {
    if (!this.popoverData) return [];
    const start = (this.currentPage - 1) * this.itemsPerPage;
    const end = start + this.itemsPerPage;
    return this.popoverData.list.slice(start, end);
  }

  get opKey() {
    return `opReason-${this.popoverData?.key || ""}`;
  }

  goToPage(page: number) {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
    }
  }

  openOverlayPanel(event: Event) {
    // Reset to first page when opening
    this.currentPage = 1;
    const refs: any = this.$refs;
    refs?.[this.opKey]?.toggle(event);
  }

  closeOverlayPanel(event: Event) {
    const refs: any = this.$refs;
    refs?.[this.opKey]?.hide(event);
  }
}
